// Vendor
import { ElementRef, SimpleChanges } from '@angular/core';
import Highcharts from 'highcharts';
import XRangeModule from 'highcharts/modules/xrange';
import * as i0 from "@angular/core";
XRangeModule(Highcharts);
export class FactorCoefficientRangeChart {
    constructor(host) {
        this.host = host;
        this.el = host.nativeElement;
    }
    ngOnChanges(changes) {
        if (changes.rangeData?.currentValue)
            this.render();
    }
    render() {
        const { rangeData } = this;
        if (!rangeData)
            return;
        const { scaleMin, scaleMax } = rangeData;
        const scaleRange = scaleMax - scaleMin;
        // Visual width of the current coefficient marker as a fraction of the scale range.
        // Keeps consistent pixel width across charts with different scales. (0.015 = 1.5%)
        const CURRENT_COEFFICIENT_MARKER_FRACTION = 0.015;
        const currentCoefficientMarkerWidth = scaleRange * CURRENT_COEFFICIENT_MARKER_FRACTION;
        // If the current security value marker would overflow past scaleMax, grow it leftward instead
        // so the axis and grey baseline never need to extend beyond the peer group scale
        const currentValueOverflowsRight = rangeData.securityCurrentCoefficient + currentCoefficientMarkerWidth > scaleMax;
        const securityCurrentX = currentValueOverflowsRight
            ? rangeData.securityCurrentCoefficient - currentCoefficientMarkerWidth
            : rangeData.securityCurrentCoefficient;
        const securityCurrentX2 = currentValueOverflowsRight
            ? rangeData.securityCurrentCoefficient
            : rangeData.securityCurrentCoefficient + currentCoefficientMarkerWidth;
        const hasCategory = rangeData.categoryAverageCoefficient !== null && rangeData.categoryAverageCoefficient !== undefined;
        // If Category average is outside scale bounds we hide it entirely
        const categoryAverageInBounds = hasCategory &&
            rangeData.categoryAverageCoefficient >= scaleMin &&
            rangeData.categoryAverageCoefficient <= scaleMax;
        // PRIMARY SECURITY HISTORICAL RANGE (light blue)
        // Guard against inverted ranges (securityMin > securityMax) and ranges
        // that have zero overlap with scale bounds. Either case gets
        // empty data — Highcharts' axis clipping does not handle these correctly.
        const historicalRangeIsValid = rangeData.securityMinCoefficient <= rangeData.securityMaxCoefficient &&
            rangeData.securityMinCoefficient <= scaleMax &&
            rangeData.securityMaxCoefficient >= scaleMin;
        // Space reserved inside the SVG for Low/High labels on each side
        const leftLabelWidth = 45;
        const rightLabelWidth = 45;
        // securityCurrentCoefficient is a point value, not a range. Only render
        // when it falls within [scaleMin, scaleMax] (inclusive). If it's outside
        // bounds we pass empty data
        const currentValueInBounds = rangeData.securityCurrentCoefficient >= scaleMin && rangeData.securityCurrentCoefficient <= scaleMax;
        this.el.innerHTML = '';
        this.el.style.display = 'block';
        this.el.style.overflow = 'hidden';
        Highcharts.chart(this.el, {
            chart: {
                type: 'xrange',
                height: 60,
                backgroundColor: 'transparent',
                spacing: [0, 0, 0, 0],
                marginLeft: leftLabelWidth,
                marginRight: rightLabelWidth,
                events: {
                    render() {
                        const chart = this;
                        // Remove previous custom elements
                        if (chart.customMarkerLine)
                            chart.customMarkerLine.destroy();
                        if (chart.customMarkerLabel)
                            chart.customMarkerLabel.destroy();
                        if (chart.leftTick)
                            chart.leftTick.destroy();
                        if (chart.rightTick)
                            chart.rightTick.destroy();
                        if (chart.leftCatLabel)
                            chart.leftCatLabel.destroy();
                        if (chart.leftScaleLabel)
                            chart.leftScaleLabel.destroy();
                        if (chart.rightCatLabel)
                            chart.rightCatLabel.destroy();
                        if (chart.rightScaleLabel)
                            chart.rightScaleLabel.destroy();
                        const midY = chart.plotTop + chart.plotHeight / 2;
                        const plotRight = chart.plotLeft + chart.plotWidth;
                        // Left labels: categoryLabelLeft on top, scaleMinLabel below
                        chart.leftCatLabel = chart.renderer
                            .text(rangeData.categoryLabelLeft, chart.plotLeft - 8, midY - 6)
                            .attr({ align: 'right' })
                            .css({ color: '#999999', fontSize: '11px' })
                            .add();
                        chart.leftScaleLabel = chart.renderer
                            .text(rangeData.scaleMinLabel, chart.plotLeft - 8, midY + 12)
                            .attr({ align: 'right' })
                            .css({ color: '#999999', fontSize: '11px' })
                            .add();
                        // Right labels: categoryLabelRight on top, scaleMaxLabel below
                        chart.rightCatLabel = chart.renderer
                            .text(rangeData.categoryLabelRight, plotRight + 8, midY - 6)
                            .attr({ align: 'left' })
                            .css({ color: '#999999', fontSize: '11px' })
                            .add();
                        chart.rightScaleLabel = chart.renderer
                            .text(rangeData.scaleMaxLabel, plotRight + 8, midY + 12)
                            .attr({ align: 'left' })
                            .css({ color: '#999999', fontSize: '11px' })
                            .add();
                        // Category average line + label — only rendered when value is within
                        // [scaleMin, scaleMax]. If it's outside bounds we skip it entirely
                        if (categoryAverageInBounds) {
                            const x = chart.xAxis[0].toPixels(rangeData.categoryAverageCoefficient, false);
                            const lineHeight = 28;
                            const yTop = chart.plotTop + 17;
                            const yBottom = yTop + lineHeight;
                            // Draw Single vertical black line
                            chart.customMarkerLine = chart.renderer
                                .path(['M', x, yTop, 'L', x, yBottom])
                                .attr({ 'stroke': '#4c4c4c', 'stroke-width': 3, 'zIndex': 20 })
                                .add();
                            // Draw Label above vertical black line
                            chart.customMarkerLabel = chart.renderer
                                .text(rangeData.categoryAverageCoefficient.toFixed(2), x, yTop - 6)
                                .attr({ align: 'center', zIndex: 25 })
                                .css({ color: '#666666', fontSize: '12px' })
                                .add();
                        }
                        // Full scale end ticks
                        const minX = chart.xAxis[0].toPixels(scaleMin, false);
                        const maxX = chart.xAxis[0].toPixels(scaleMax, false);
                        const baselineY = chart.plotTop + 40;
                        const tickHeight = 22;
                        chart.leftTick = chart.renderer
                            .path(['M', minX, baselineY - tickHeight, 'L', minX, baselineY])
                            .attr({ 'stroke': '#bfbfbf', 'stroke-width': 2, 'zIndex': 10 })
                            .add();
                        chart.rightTick = chart.renderer
                            .path(['M', maxX, baselineY - tickHeight, 'L', maxX, baselineY])
                            .attr({ 'stroke': '#bfbfbf', 'stroke-width': 2, 'zIndex': 10 })
                            .add();
                    },
                },
            },
            title: { text: '' },
            credits: { enabled: false },
            legend: { enabled: false },
            tooltip: { enabled: false },
            xAxis: {
                gridLineWidth: 0,
                min: scaleMin,
                max: scaleMax,
                lineWidth: 0,
                tickLength: 0,
                labels: { enabled: false },
            },
            yAxis: {
                visible: false,
                min: -0.5,
                max: 0.5,
                categories: [''],
            },
            plotOptions: {
                series: {
                    states: { inactive: { opacity: 1 } },
                },
                xrange: {
                    grouping: false,
                    groupPadding: 0,
                    pointPadding: 0,
                    borderWidth: 0,
                    borderColor: 'transparent',
                    borderRadius: 0,
                    crisp: true,
                },
            },
            series: [
                // THIN GREY BASELINE
                {
                    type: 'xrange',
                    borderWidth: 0,
                    pointPadding: 0,
                    groupPadding: 0,
                    pointWidth: 3.3,
                    data: [{ x: scaleMin, x2: scaleMax, y: 0, color: '#bfbfbf' }],
                    zIndex: 1,
                },
                // PRIMARY SECURITY HISTORICAL RANGE (light blue)
                {
                    type: 'xrange',
                    borderWidth: 0,
                    pointPadding: 0,
                    groupPadding: 0,
                    pointWidth: 18,
                    data: historicalRangeIsValid
                        ? [
                            {
                                x: rangeData.securityMinCoefficient,
                                x2: rangeData.securityMaxCoefficient,
                                y: 0,
                                color: '#BAD1EE',
                            },
                        ]
                        : [],
                    zIndex: 2,
                },
                // PRIMARY SECURITY CURRENT VALUE (dark blue)
                {
                    type: 'xrange',
                    borderWidth: 0,
                    pointPadding: 0,
                    groupPadding: 0,
                    pointWidth: 18,
                    data: currentValueInBounds
                        ? [{ x: securityCurrentX, x2: securityCurrentX2, y: 0, color: '#578CD8' }]
                        : [],
                    zIndex: 3,
                },
            ],
        });
    }
}
FactorCoefficientRangeChart.ɵfac = function FactorCoefficientRangeChart_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || FactorCoefficientRangeChart)(i0.ɵɵdirectiveInject(i0.ElementRef)); };
FactorCoefficientRangeChart.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: FactorCoefficientRangeChart, selectors: [["ycn-factor-coefficient-range"]], inputs: { rangeData: "rangeData" }, standalone: false, features: [i0.ɵɵNgOnChangesFeature], decls: 0, vars: 0, template: function FactorCoefficientRangeChart_Template(rf, ctx) { }, encapsulation: 2 });
